import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  User,
  Building2,
  Cloud,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import WorldGlobe from '../components/ui/WorldGlobe';
import AdaptiveLearningCard from '../components/settings/AdaptiveLearningCard';
import CompaniesCard from '../components/settings/CompaniesCard';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

// P-SF2: the backend's own result codes, in words. Salesforce's messages never reach the
// screen, and a code we do not recognise still gets a sentence instead of its raw value.
const CALLBACK_ERRORS = {
  invalid_state: 'That connection request had already been used or was not recognised. Please try again.',
  state_expired: 'The connection request timed out. Please try again.',
  access_denied: 'Access was declined in Salesforce, so nothing was connected.',
  authorization_failed: 'Salesforce did not complete the authorisation. Please try again.',
  user_not_authorized: 'Your account is no longer allowed to connect Salesforce.',
  token_exchange_failed: 'Salesforce refused the connection. Please try again.',
  token_response_invalid: 'Salesforce did not return a usable connection. Please try again.',
  refresh_token_missing: 'Salesforce did not grant offline access, so the connection was not kept.',
  instance_url_invalid: 'Salesforce answered with an unexpected address, so nothing was saved.',
  salesforce_unavailable: 'Salesforce could not be reached. Please try again.',
  storage_failed: 'The connection could not be saved. Please try again.',
};
const CALLBACK_FALLBACK = 'The Salesforce connection did not complete. Please try again.';

const formatDay = (value) => {
  if (!value) return null;
  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return null;
  return moment.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Settings = () => {
  const { user, token, isAdmin } = useAuth();

  // P-SF2: "loading" | "ready" | "unavailable" | "error". "unavailable" is a 404, which is what
  // the API answers while the integration is switched off on the server: the card then stays
  // hidden instead of showing a failure nobody can act on.
  const [connectionState, setConnectionState] = useState('loading');
  const [connection, setConnection] = useState(null);
  const [working, setWorking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadConnection = useCallback(async () => {
    if (!token) return;
    setConnectionState('loading');
    try {
      const response = await axios.get(`${API_URL}/salesforce/connection`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConnection(response.data);
      setConnectionState('ready');
    } catch (error) {
      setConnectionState(error.response?.status === 404 ? 'unavailable' : 'error');
    }
  }, [token]);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  // P-SF2: the OAuth callback sends the browser back to /settings with its result. It is shown
  // once and then removed from the URL, so a refresh does not replay it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('salesforce');
    if (!result) return;
    if (result === 'connected') {
      toast.success('Salesforce connected');
    } else {
      toast.error(CALLBACK_ERRORS[params.get('reason')] || CALLBACK_FALLBACK);
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const startConnect = async () => {
    setWorking(true);
    try {
      const response = await axios.post(`${API_URL}/salesforce/oauth/start`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const authorizeUrl = response.data?.authorize_url;
      if (!authorizeUrl) throw new Error('no authorize url');
      // The SAME tab, deliberately: the callback returns to this CiaoLead session.
      window.location.assign(authorizeUrl);
    } catch (error) {
      setWorking(false);
      const status = error.response?.status;
      if (status === 403) {
        toast.error('Only an administrator of this workspace can connect Salesforce.');
      } else if (status === 404 || status === 503) {
        toast.error('The Salesforce integration is not available on this server.');
      } else {
        toast.error('Could not start the Salesforce connection. Please try again.');
      }
    }
  };

  const disconnect = async () => {
    setWorking(true);
    try {
      const response = await axios.delete(`${API_URL}/salesforce/connection`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Salesforce disconnected', response.data?.revoked === false
        ? { description: 'Salesforce did not confirm that the access was revoked.' }
        : undefined);
      setConfirmOpen(false);
      await loadConnection();
    } catch (error) {
      toast.error('Could not disconnect Salesforce. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const status = connection?.status;
  const instance = (connection?.instance_url || '').replace(/^https?:\/\//, '');
  const connectedOn = formatDay(connection?.connected_at);
  const verifiedOn = formatDay(connection?.last_verified_at);

  const renderSalesforce = () => {
    if (connectionState === 'loading') {
      return (
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="salesforce-loading">
          <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
          Checking the Salesforce connection...
        </div>
      );
    }

    if (connectionState === 'error') {
      return (
        <div className="space-y-3" data-testid="salesforce-error">
          <p className="text-sm text-slate-400">
            The Salesforce connection status could not be loaded.
          </p>
          <Button variant="outline" size="sm" onClick={loadConnection} data-testid="salesforce-retry-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try again
          </Button>
        </div>
      );
    }

    if (status === 'connected') {
      return (
        <div className="space-y-4" data-testid="salesforce-connected">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="bg-green-500/10 text-green-300 border-green-500/40">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Connected
            </Badge>
            {instance && (
              <span className="text-sm text-slate-300" data-testid="salesforce-instance">{instance}</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {connection?.org_id && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-400">Salesforce org</p>
                <p className="text-sm text-white break-all" data-testid="salesforce-org">{connection.org_id}</p>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-400">Connected on</p>
              <p className="text-sm text-white" data-testid="salesforce-connected-on">{connectedOn || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-400">Last verified</p>
              <p className="text-sm text-white" data-testid="salesforce-verified-on">{verifiedOn || 'Not verified yet'}</p>
            </div>
          </div>
          {isAdmin ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={working}
              className="border-red-500/40 text-red-400 hover:bg-red-500/15"
              data-testid="salesforce-disconnect-btn"
            >
              Disconnect
            </Button>
          ) : (
            <p className="text-sm text-slate-400" data-testid="salesforce-readonly">
              Only an administrator of this workspace can change this connection.
            </p>
          )}
        </div>
      );
    }

    if (status === 'reauth_required') {
      return (
        <div className="space-y-4" data-testid="salesforce-reauth">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="bg-amber-500/10 text-amber-300 border-amber-500/40">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Authorisation needed
            </Badge>
            {instance && <span className="text-sm text-slate-300">{instance}</span>}
          </div>
          <p className="text-sm text-slate-400">
            Salesforce stopped accepting this connection, so it has to be authorised again.
          </p>
          {isAdmin ? (
            <Button
              onClick={startConnect}
              disabled={working}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="salesforce-reconnect-btn"
            >
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Reconnect Salesforce
            </Button>
          ) : (
            <p className="text-sm text-slate-400" data-testid="salesforce-readonly">
              Ask an administrator of this workspace to authorise Salesforce again.
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4" data-testid="salesforce-not-connected">
        <Badge variant="outline" className="bg-white/5 text-slate-300 border-white/20">
          Not connected
        </Badge>
        {isAdmin ? (
          <>
            <p className="text-sm text-slate-400">
              Connecting opens Salesforce in this tab, where you sign in and allow access.
            </p>
            <Button
              onClick={startConnect}
              disabled={working}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="salesforce-connect-btn"
            >
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
              Connect Salesforce
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-400" data-testid="salesforce-readonly">
            Ask an administrator of this workspace to connect Salesforce.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 relative" data-testid="settings-page">
      <WorldGlobe size={300} className="hidden lg:block opacity-70" style={{ top: '-50px', right: '-30px' }} />
      {/* Profile Section */}
      <Card className="world-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
              <User className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Profile</CardTitle>
              <CardDescription className="text-slate-400">
                Your account information
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-400">Email</p>
                <p className="text-sm text-white" data-testid="settings-email">
                  {user?.email}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-400">Role</p>
                <p className="text-sm text-white capitalize" data-testid="settings-role">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Section */}
      <Card className="world-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
              <Building2 className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Company</CardTitle>
              <CardDescription className="text-slate-400">
                Your company workspace
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-400">Company Name</p>
              <p className="text-sm text-white" data-testid="settings-company-name">
                {user?.company_name}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PILOT-ACCESS-002: the platform super-admin's Company registry; renders nothing, and
          fetches nothing, for anyone else. */}
      <CompaniesCard />

      {/* S5-028: the company's Adaptive Learning preference (admin-only switch, read-only for members). */}
      <AdaptiveLearningCard />

      {/* P-SF2: Salesforce connection. Hidden entirely while the integration is off on the
          server, because there is nothing a user could do about it. */}
      {connectionState !== 'unavailable' && (
        <Card className="world-card" data-testid="salesforce-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
                <Cloud className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Salesforce</CardTitle>
                <CardDescription className="text-slate-400">
                  Connect this company workspace to your Salesforce org
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>{renderSalesforce()}</CardContent>
        </Card>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="salesforce-disconnect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Salesforce?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                CiaoLead will stop using this Salesforce org, and the stored access is removed.
              </span>
              <span className="block text-slate-300 font-medium">
                Nothing in Salesforce is changed or deleted.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working} data-testid="salesforce-disconnect-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                disconnect();
              }}
              disabled={working}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="salesforce-disconnect-confirm-btn"
            >
              {working ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default Settings;
